import React from 'react';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import Divider from '@/components/atom/Divider';
import QnaStickyWriteButton from '@/components/common/QnaStickyWriteButton';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import BottomActionSheet from '@/components/molecules/BottomActionSheet';
import ReportSelectModal from '@/components/molecules/Modal/SelectModal/ReportSelectModal';
import StickyAppHeader from '@/components/molecules/StickyAppHeader';
import StickyMissionHeader from '@/components/molecules/StickyMissionHeader';
import QuestionPaymentInfo from '@/components/templates/mobile/communication/Qna/QnaDetailMobileTemplate/QuestionPaymentInfo/QuestionPaymentInfo';
import useQuestionDetailViewModel from '@/hooks/qna/useQuestionDetailViewModel';
import { isQnaMyQuestionPaid, isQnaMyQuestionPrivate } from '@/services/qna/mobile';
import theme from '@/styles/theme';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { historyBack } from '@/utils/mobile/RNPlugins';
import { cn } from '@/utils/style';

import AdExchangeComponent from '@/components/common/AdExchangeComponent';
import AppInstallTopBanner from '@/components/templates/mobile/communication/Common/AppInstallTopBanner';
import useAppInstallTopBanner from '@/hooks/Cuj/useAppInstallTopBanner';
import { QnaDetailVo } from '@/types/qna';
import styled from 'styled-components';
import AppInstallBanner from '../../Common/AppInstallBanner';
import NearQuestions from './NearQuestions';
import QnaAdFallback from './QnaAdFallback';
import QnaAnswers from './QnaAnswers';
import QnaRelatedQuestion from './QnaRelatedQuestion';
import QuestionDetail from './QuestionDetail';

interface Props extends CustomPageProps {
  viewModel: Omit<ReturnType<typeof useQuestionDetailViewModel>, 'qnaDetail'> & {
    qnaDetail: QnaDetailVo;
  };
  topBannerViewModel: ReturnType<typeof useAppInstallTopBanner>;
}

const QnaDetailMobileTemplate = ({ viewModel, client, topBannerViewModel }: Props) => {
  return (
    <TemplateLayout.Wrapper>
      {client.isWebView && (
        <TemplateLayout.Header useSafeArea>
          <StickyAppHeader title={viewModel.qnaDetail.category.name} onClickBackButton={viewModel.goBack} />
        </TemplateLayout.Header>
      )}
      {client.clientType === ClientType.MOBILE_WEB && topBannerViewModel.isVisible && (
        <AppInstallTopBanner
          onLogEvent={viewModel.onClickTopCta}
          onClickClose={topBannerViewModel.hideBanner}
          content='web_top_bnr_details'
          trackingInfo={{
            campaign: 'web_content_qna',
            contentId: viewModel.qnaDetail.questionPid,
            contentTitle: viewModel.qnaDetail.title,
            contentCategory: viewModel.qnaDetail.category.name,
          }}
        />
      )}
      {client.isWebView && viewModel.timerVisible && (
        <StickyMissionHeader
          displayTime={viewModel.displayTime}
          targetTime={viewModel.missionTargetTime}
          missionCompleted={viewModel.missionCompleted}
          clickable={viewModel.missionCompleted}
          onClick={historyBack}
        />
      )}
      <TemplateLayout.Content>
        {client.clientType === ClientType.WEB && (
          <BreadCrumbsWrapper>
            <BreadCrumbs
              visible
              name={viewModel.qnaDetail.category.name}
              path={`${ROUTES.QNA.DETAIL.path}/realtime`}
              type='qna'
              urlParameter={`?categoryId=${viewModel.qnaDetail.category.questionCategoryId}`}
            />
          </BreadCrumbsWrapper>
        )}
        <Container isWebview={client.isWebView}>
          {/* 질문 */}
          <QuestionDetail
            onClickAskExpertAnswer={viewModel.moveToRepayPage}
            questionDetail={viewModel.qnaDetail}
            onShowModal={viewModel.onShowModal}
            qnaAttachments={viewModel.qnaAttachments}
            onClickImage={viewModel.onClickImage}
            imageZoomIndex={viewModel.imageZoomIndex}
            onCloseImageZoom={viewModel.onCloseImageZoom}
            onClickLikeQuestion={viewModel.onClickLikeQuestion}
            pageName={viewModel.pageName}
            onShareModal={viewModel.onShareModal}
            onChangeShowShareModal={viewModel.onChangeShowShareModal}
            user={viewModel.user}
            imageSlideIdx={viewModel.imageSlideIdx}
            onChangeImageBlindType={viewModel.onChangeImageBlindType}
            onChangeSlideImage={viewModel.onChangeSlideImage}
            isMyQuestion={viewModel.isMyQuestion}
          />
          <AdExchangeComponent
            fallback={
              <QnaAdFallback
                client={client}
                questionBannerInfo={viewModel.questionBannerInfo}
                onClick={viewModel.onClickBanner}
              />
            }
            bannerAdExchange={viewModel.bannerAdExchange}
            onImpressionLog={viewModel.handleSendExposureLog}
          >
            <AdExchangeComponent.Content
              className={cn('px-[24px]', {
                'py-[20px]': !client.isMobile,
                'pb-[20px]': client.isMobile,
              })}
            >
              <AdExchangeComponent.ImageAd
                className={cn('w-full rounded-[12px] object-contain', {
                  'aspect-[720/120]': !client.isMobile,
                  'aspect-[312/96]': client.isMobile,
                })}
              />
              <AdExchangeComponent.DeliberatedCode />
            </AdExchangeComponent.Content>
          </AdExchangeComponent>
          <Divider height={'15px'} color={theme.color.gray30} />
          {/* 답변 */}
          {!!viewModel.qnaDetail.answers.length && (
            <QnaAnswers
              qnaDetail={viewModel.qnaDetail}
              onShowModal={viewModel.onShowModal}
              onClickLikeAnswer={viewModel.onClickLikeAnswer}
              onClickMagazine={viewModel.onClickMagazine}
              onClickWriteReview={viewModel.onClickWriteReview}
              moveToHospitalDetail={viewModel.moveToHospitalDetail}
              moveToDoctorDetailPage={viewModel.moveToDoctorDetailPage}
            />
          )}
          {viewModel.isMyQuestion &&
            viewModel.qnaDetail.paymentInfo &&
            isQnaMyQuestionPaid(viewModel.qnaDetail.status) && (
              <QuestionPaymentInfo
                paidAt={viewModel.qnaDetail.paymentInfo.paidAt}
                price={viewModel.qnaDetail.paymentInfo.price}
                discountPrice={viewModel.qnaDetail.paymentInfo.discountAmount}
                cardName={viewModel.qnaDetail.paymentTotal?.userPayment?.cardName || ''}
                cardNumber={viewModel.qnaDetail.paymentTotal?.userPayment?.cardNumber || ''}
                pointUsed={viewModel.qnaDetail.paymentInfo.usedPoint}
                paidAmount={viewModel.qnaDetail.paymentInfo.paidAmount}
              />
            )}

          {/* 관련 질문 */}
          <QnaRelatedQuestion
            categoryName={viewModel.qnaDetail.category.name}
            relatedQna={viewModel.relatedQna}
            onClickRelatedQna={viewModel.onClickRelatedQna}
          />

          {/* 이전/다음 글 */}
          <NearQuestions
            nearQuestions={viewModel.qnaDetail.nearSimpleQuestions}
            onClickNearQna={viewModel.onClickNearQna}
          />

          {/* 웹투앱 유도 배너 */}
          {!client.isWebView && (
            <AppInstallBanner
              headText='내게 딱 맞는 정보가 없다면?'
              mainTextRows={['닥터나우에서 진료부터', '약 처방까지 받아보세요']}
              trackingInfo={{
                campaign: 'web_content_qna',
                contentId: viewModel.qnaDetail.questionPid,
                contentTitle: viewModel.qnaDetail.title,
                contentCategory: viewModel.qnaDetail.category.name,
              }}
            />
          )}

          {/* 질문 신고하기 */}
          {viewModel.reportItems && (
            <ReportSelectModal
              isVisible={viewModel.showReportModal}
              onClose={viewModel.onCloseReportModal}
              selectList={viewModel.reportItems}
            />
          )}

          {/* 질문 비공개하기 */}
          <BottomActionSheet
            isOpened={viewModel.isShowPrivateModal}
            onClickOverlay={viewModel.toggleIsShowPrivateModal}
          >
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col gap-2 px-3'>
                <div className='typography-ui-20sb text-gray-800'>내 질문 관리</div>
                <div className='typography-ui-15r text-gray-600'>비공개로 전환 후 다시 공개로 되돌릴 수 없어요.</div>
              </div>
              <button
                className={cn([
                  'typography-ui-17m flex w-full justify-start px-3 py-4 text-gray-600',
                  'disabled:text-gray-100',
                ])}
                disabled={isQnaMyQuestionPrivate(viewModel.qnaDetail.accessibleType)}
                onClick={viewModel.onClickQuestionPrivate}
              >
                비공개로 전환하기
              </button>
            </div>
          </BottomActionSheet>
        </Container>
        <QnaStickyWriteButton
          buttonText={viewModel.getStickyButtonText()}
          pageName={viewModel.pageName}
          sectionName={viewModel.pageName}
          isPaymentFailed={viewModel.isPaymentFailed}
          onClickRepay={viewModel.moveToRepayPage}
          shouldShowEventText={viewModel.shouldShowEventText()}
          onClickButton={viewModel.onClickStickButton}
          questionCategory={viewModel.targetQuestionCategory}
        />
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default QnaDetailMobileTemplate;

const Container = styled.div<{ isWebview: boolean }>`
  ${({ theme, isWebview }) => `
    width: 100%;
    height: auto;
    min-height: ${isWebview ? '100vh' : '0'};
    background: ${theme.color.white};
  `};

  position: relative;
`;

const BreadCrumbsWrapper = styled.div`
  padding: 26px 24px 0 24px;
`;
